import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileCheck, BookOpen, Music, Image, Video } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { copyrightPricing } from '../../pricingData';
import { PricingSection } from '../../components/PricingSection';

export const CopyrightRegistration = ({ onOpenPopup }) => {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Copyright Registration Services | Protect Your Creative Works</title>
        <meta name="description" content="Register your copyright with expert assistance. Protect your original creative works including books, music, art, software, and more with official US Copyright Office registration." />
        <meta name="keywords" content="copyright registration, protect creative works, US Copyright Office, copyright protection, register copyright, intellectual property" />
      </Helmet>

      {/* Creative Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float">
            <FileCheck className="w-20 h-20 text-white/10" />
          </div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <BookOpen className="w-24 h-24 text-white/10" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">📝 Creative Protection</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Copyright Registration</h1>
            <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto animate-slide-in">
              Protect your original creative works with official copyright registration
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-primary hover:bg-gray-100 h-12 px-8 text-base hover:scale-105 transition-all"
              >
                Register Your Copyright
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base"
                onClick={() => document.getElementById('what-we-protect').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* What We Protect with Image */}
      <section id="what-we-protect" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">What is Copyright Registration?</h2>
                <div className="text-base text-gray-700 space-y-4 leading-relaxed">
                  <p>
                    Copyright protects original works of authorship including literary, dramatic, musical, and artistic works. Registration with the U.S. Copyright Office establishes a public record of your copyright claim and provides legal benefits in case of infringement.
                  </p>
                  <p>
                    While copyright exists from the moment of creation, registration is necessary to enforce your rights through legal action and provides additional statutory benefits that strengthen your protection.
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2/pexels-photo-3727463.jpeg" 
                  alt="Creative work" 
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Types of Works We Protect</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Literary Works</h4>
                <p className="text-sm text-gray-600">Books, articles, poems, manuscripts, software code</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Music className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Musical Works</h4>
                <p className="text-sm text-gray-600">Songs, compositions, lyrics, recordings</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Image className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Visual Arts</h4>
                <p className="text-sm text-gray-600">Paintings, photographs, sculptures, graphics</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Video className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Audiovisual Works</h4>
                <p className="text-sm text-gray-600">Films, videos, animations, presentations</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Digital Content</h4>
                <p className="text-sm text-gray-600">Websites, apps, digital art, online courses</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="text-orange-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Dramatic Works</h4>
                <p className="text-sm text-gray-600">Plays, screenplays, scripts, choreography</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Register Your Copyright?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Protection</h3>
              <p className="text-base text-gray-600">
                Registration is required before you can file a copyright infringement lawsuit in federal court
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Public Record</h3>
              <p className="text-base text-gray-600">
                Creates a public record of your copyright ownership and the date of creation
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Statutory Damages</h3>
              <p className="text-base text-gray-600">
                Allows you to recover statutory damages and attorney's fees in infringement cases
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Protect Your Creative Work Today</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Don't leave your intellectual property unprotected. Get started with copyright registration now.
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-gray-100 h-12 px-8 text-base"
          >
            Get Started
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      
      <div id="pricing"><PricingSection pricingData={copyrightPricing} onOpenPopup={onOpenPopup} /></div>

      <TestimonialSlider />
    </div>
  );
};